import React, {
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { FeedbackQuestionHandler, FeedbackSectionInfo } from '../utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FeedbackQuestion } from '../api';

const stringQuestionIds = [40006, 40007, 40008, 40009];

const isQuestionRadio = (question: FeedbackQuestion) => {
  return (
    stringQuestionIds.includes(question.dbId) ||
    question.questionType === 'RADIO'
  );
};

const getAnswer = (
  question: FeedbackQuestion,
  answer: string,
): string[] | string => {
  if (isQuestionRadio(question)) {
    return answer || '';
  }
  if (!answer) return [];
  try {
    return JSON.parse(answer.replace(/'/g, '"'));
  } catch (e) {
    return [];
  }
};

const StyledDiv = styled.div``;
const defaultGetLabel = (value: string) => value;

const CheckboxSection = React.forwardRef<
  FeedbackQuestionHandler,
  FeedbackSectionInfo
>(
  (
    { reportId, questionId, answer, questionText, question, beneficiaryTerm },
    forwardRef,
  ) => {
    const [selected, setSelected] = useState<string[] | string>(
      getAnswer(question, answer),
    );

    useEffect(() => {
      setSelected(getAnswer(question, answer));
    }, [answer]);

    const { options, isRadioQuestion } = useMemo(() => {
      return {
        isRadioQuestion: isQuestionRadio(question),
        options: question.enumResponses.map(o => {
          if (beneficiaryTerm) {
            o.response = o.response.replace(
              /<Primary_beneficiary>/g,
              beneficiaryTerm,
            );
          }
          return o.response;
        }),
      };
    }, [question, beneficiaryTerm]);

    useImperativeHandle(forwardRef, () => ({
      reportId,
      questionId,
      answer: () =>
        typeof selected === 'string' ? selected : JSON.stringify(selected),
      errorText: () =>
        selected && selected.length > 0
          ? undefined
          : 'Please select one of the options',
    }));

    const handleChange = useCallback(
      (value: string) => {
        if (isRadioQuestion) {
          setSelected(value);
          return;
        }
        setSelected(s => {
          const newState = [...s];
          const index = newState.indexOf(value);
          if (index === -1) {
            newState.push(value);
          } else {
            newState.splice(index, 1);
          }
          return newState;
        });
      },
      [isRadioQuestion, setSelected],
    );

    return (
      <StyledDiv>
        <TrueImpactText variant="heading-subheading" className="question-text">
          {questionText}
        </TrueImpactText>
        <SelectorOptions
          variant={isRadioQuestion ? 'radio' : 'checkbox'}
          padding={isRadioQuestion ? SPACERS.small : SPACERS.tiny}
          options={options}
          getLabel={defaultGetLabel}
          getSelected={val =>
            typeof selected === 'string'
              ? val === selected
              : selected.includes(val)
          }
          onClick={handleChange}
        />
      </StyledDiv>
    );
  },
);

export default CheckboxSection;
